import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Trash2, Loader2, Image as ImageIcon, Upload, Settings, Edit, X, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGallerySection = () => {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({ url: '', category: '', alt: '' });
  const [uploading, setUploading] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [deleteCat, setDeleteCat] = useState(null);
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [imgRes, catRes] = await Promise.all([
        axios.get(`${API}/gallery`),
        axios.get(`${API}/gallery/categories`),
      ]);
      setImages(imgRes.data);
      setCategories(catRes.data);
      if (catRes.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: catRes.data[0].slug }));
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, url: `${BACKEND_URL}${res.data.url}` });
      toast.success('Immagine caricata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.url) { toast.error('Carica un\'immagine o incolla un URL'); return; }
    try {
      await axios.post(`${API}/gallery`, formData, { headers: getAuthHeaders() });
      toast.success('Immagine aggiunta!');
      setDialogOpen(false);
      setFormData({ url: '', category: categories[0]?.slug || '', alt: '' });
      fetchAll();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDeleteImage = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/gallery/${deleteTarget.id}`, { headers: getAuthHeaders() });
      toast.success('Immagine eliminata!');
      fetchAll();
    } catch (error) {
      toast.error('Errore');
    }
    setDeleteTarget(null);
  };

  // Category management
  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await axios.post(`${API}/gallery/categories`, { name: newCatName.trim() }, { headers: getAuthHeaders() });
      toast.success('Categoria aggiunta!');
      setNewCatName('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleUpdateCategory = async (catId) => {
    if (!editCatName.trim()) return;
    try {
      await axios.put(`${API}/gallery/categories/${catId}`, { name: editCatName.trim() }, { headers: getAuthHeaders() });
      toast.success('Categoria aggiornata!');
      setEditingCat(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCat) return;
    try {
      await axios.delete(`${API}/gallery/categories/${deleteCat.id}`, { headers: getAuthHeaders() });
      toast.success('Categoria eliminata!');
      setDeleteCat(null);
      if (activeCategory === deleteCat.slug) setActiveCategory('all');
      fetchAll();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const filteredImages = activeCategory === 'all' ? images : images.filter(img => img.category === activeCategory);

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="gallery-section">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Gestione Galleria</h2>
          <p className="text-muted-foreground text-xs mt-1">{images.length} immagini</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setCatDialogOpen(true)} data-testid="manage-categories-btn">
            <Settings className="h-4 w-4 mr-1" /> Categorie
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setFormData({ url: '', category: categories[0]?.slug || '', alt: '' }); }}>
            <DialogTrigger asChild>
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="add-image-btn">
                <Plus className="h-4 w-4 mr-1" /> Aggiungi
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Aggiungi Immagine</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
                  {formData.url ? (
                    <div className="space-y-2">
                      <img src={formData.url} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                      <Button type="button" variant="outline" size="sm" onClick={() => setFormData({ ...formData, url: '' })}>Cambia</Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
                        {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Caricamento...</> : <><Upload className="h-4 w-4 mr-2" /> Carica da dispositivo</>}
                      </Button>
                      <p className="text-xs text-muted-foreground">JPG, PNG, WebP, GIF</p>
                    </div>
                  )}
                </div>
                {!formData.url && (
                  <div>
                    <Label>Oppure incolla URL</Label>
                    <Input value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://esempio.com/img.jpg" data-testid="image-url-input" />
                  </div>
                )}
                <div>
                  <Label>Categoria</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger><SelectValue placeholder="Scegli categoria" /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (
                        <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Descrizione</Label>
                  <Input value={formData.alt} onChange={(e) => setFormData({ ...formData, alt: e.target.value })} placeholder="Descrizione immagine" />
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Annulla</Button>
                  <Button type="submit" className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="submit-image-btn">Aggiungi</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Category filter tabs */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all border ${
            activeCategory === 'all' ? 'bg-foreground text-background border-foreground' : 'bg-background text-foreground border-border hover:border-foreground/30'
          }`}
          data-testid="gallery-cat-all"
        >
          Tutti ({images.length})
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.slug)}
            className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all border ${
              activeCategory === cat.slug ? 'bg-foreground text-background border-foreground' : 'bg-background text-foreground border-border hover:border-foreground/30'
            }`}
            data-testid={`gallery-cat-${cat.slug}`}
          >
            {cat.name} ({images.filter(i => i.category === cat.slug).length})
          </button>
        ))}
      </div>

      {/* Images grid */}
      {filteredImages.length === 0 ? (
        <div className="text-center py-12">
          <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">Nessuna immagine</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredImages.map((image) => (
            <Card key={image.id} className="card-elegant overflow-hidden group relative" data-testid={`gallery-img-${image.id}`}>
              <div className="relative aspect-square">
                <img src={image.url} alt={image.alt || 'Gallery'} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-2.5">
                  <span className="text-xs text-muted-foreground truncate">{image.alt}</span>
                  <Button variant="destructive" size="sm" className="h-7 px-2 flex-shrink-0" onClick={() => setDeleteTarget(image)} data-testid={`delete-img-${image.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Category Management Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Gestione Categorie</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Add new category */}
            <div className="flex gap-2">
              <Input
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder="Nuova categoria..."
                className="text-base"
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                data-testid="new-category-input"
              />
              <Button onClick={handleAddCategory} className="bg-accent text-accent-foreground hover:bg-accent/90 flex-shrink-0" data-testid="add-category-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* List of categories */}
            <div className="space-y-2">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-2 p-2.5 bg-secondary rounded-lg" data-testid={`cat-item-${cat.id}`}>
                  {editingCat === cat.id ? (
                    <>
                      <Input
                        value={editCatName}
                        onChange={(e) => setEditCatName(e.target.value)}
                        className="text-sm h-8 flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdateCategory(cat.id)}
                        autoFocus
                        data-testid={`edit-cat-input-${cat.id}`}
                      />
                      <Button size="sm" className="h-8 w-8 p-0 bg-accent text-accent-foreground" onClick={() => handleUpdateCategory(cat.id)} data-testid={`save-cat-${cat.id}`}>
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setEditingCat(null)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm font-medium">{cat.name}</span>
                      <span className="text-xs text-muted-foreground">{images.filter(i => i.category === cat.slug).length} foto</span>
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => { setEditingCat(cat.id); setEditCatName(cat.name); }} data-testid={`edit-cat-${cat.id}`}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30" onClick={() => setDeleteCat(cat)} data-testid={`delete-cat-${cat.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
              {categories.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-4">Nessuna categoria. Aggiungine una!</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Image Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa immagine?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione non puo essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTarget?.url && (
            <div className="rounded-lg overflow-hidden h-32">
              <img src={deleteTarget.url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-gallery">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteImage} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-gallery">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={!!deleteCat} onOpenChange={(open) => { if (!open) setDeleteCat(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la categoria "{deleteCat?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {images.filter(i => i.category === deleteCat?.slug).length > 0
                ? `Le ${images.filter(i => i.category === deleteCat?.slug).length} immagini in questa categoria verranno spostate in "Altro".`
                : 'Questa categoria non contiene immagini.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-cat">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-cat">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminGallerySection;
